'use client';

import * as DS from '@aurorasolar/ds';

import { STATES_ABBR_TO_FULL } from '@/models/State';

import { supportedStateCodes } from '../engine';

export type InputMode = 'KWH' | 'USD';

type Props = {
  amount: string;
  inputMode: InputMode;
  onAmountChange: (amount: string) => void;
  onInputModeChange: (mode: InputMode) => void;
  onStateChange: (stateCode: string) => void;
  stateCode: string;
};

// Unit selector reads as a compact dropdown attached to the bill box (DS Figma
// 2867-12243), matching the state selector's dropdown behavior.
const MODE_LABELS: Record<InputMode, string> = { KWH: 'kWh/month', USD: '$/month' };
const MODE_OPTIONS: DS.IDropdownOption[] = (['USD', 'KWH'] satisfies InputMode[]).map((value) => ({
  label: MODE_LABELS[value],
  value,
}));

// The engine decides which states are projectable — the picker just presents them
const STATE_OPTIONS: DS.IDropdownOption[] = supportedStateCodes
  .map((abbr) => ({
    label: STATES_ABBR_TO_FULL[abbr],
    value: abbr,
  }))
  .sort((a, b) => a.label.localeCompare(b.label));

// Shared DS input-label treatment (body14, medium, heading color) so the bill
// and state labels match the design system.
const LABEL_PROPS = { color: 'uiHeadingCopy', fontWeight: 'medium', text: 'body14' } as const;

// Keeps a label in the a11y tree without showing it (the unit selector has no
// visible label in the design).
const VISUALLY_HIDDEN = {
  border: 0,
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: '1px',
  margin: '-1px',
  overflow: 'hidden',
  padding: 0,
  position: 'absolute',
  whiteSpace: 'nowrap',
  width: '1px',
} as const;

export const BillInputs = ({
  amount,
  inputMode,
  onAmountChange,
  onInputModeChange,
  onStateChange,
  stateCode,
}: Props) => {
  const isUsd = inputMode === 'USD';
  // Default state shows "0" (via placeholder) in the muted color; a real entry
  // switches to the filled color — persistent $ prefix follows the same state.
  const isFilled = Number(amount) > 0;
  const valueColor = isFilled ? 'uiFormInput.filledTextColor' : 'uiFormInput.defaultTextColor';

  return (
    <DS.Flex gap={4} maxW="420px" mx="auto" textAlign="left" w="100%">
      <DS.Flex gap={2} w="100%">
        <DS.Text as="label" htmlFor="bill-projection-amount" {...LABEL_PROPS}>
          {isUsd ? 'Average monthly electric bill' : 'Average monthly usage'}
        </DS.Text>
        <DS.Flex alignItems="flex-end" flexDirection="row" gap={2} w="100%">
          <DS.Flex flex={1} minW={0} position="relative">
            {isUsd ? (
              <DS.Text
                aria-hidden
                color={valueColor}
                left={12}
                pointerEvents="none"
                position="absolute"
                style={{ fontSize: '16px' }}
                top="50%"
                transform="translateY(-50%)"
                zIndex="above"
              >
                $
              </DS.Text>
            ) : null}
            <DS.InputPrimitive
              $size="md"
              borderRadius="default"
              color={valueColor}
              id="bill-projection-amount"
              inputMode="numeric"
              min={0}
              onChange={(event) => onAmountChange(event.target.value.replace(/[^\d.]/g, ''))}
              placeholder="0"
              // 16px via inline style: the [data-input-size='md'] selector outspecifies the xstyled fontSize prop.
              // paddingLeft clears the persistent $ prefix with breathing room before the number.
              style={{ fontSize: '16px', paddingLeft: isUsd ? 28 : undefined }}
              value={amount}
              w="100%"
            />
          </DS.Flex>
          <DS.Flex flexShrink={0} minW={0} w="124px">
            <DS.Text as="label" id="bill-projection-unit-label" style={VISUALLY_HIDDEN}>
              Bill unit
            </DS.Text>
            <DS.Dropdown
              fullWidth
              handleSelectionChange={([selected]) => {
                if (selected) {
                  onInputModeChange(String(selected) as InputMode);
                }
              }}
              labelId="bill-projection-unit-label"
              options={MODE_OPTIONS}
              showSelectionInTrigger
              size="md"
              value={[inputMode]}
            />
          </DS.Flex>
        </DS.Flex>
      </DS.Flex>

      <DS.Flex gap={2} w="100%">
        <DS.Text as="label" id="bill-projection-state-label" {...LABEL_PROPS}>
          State
        </DS.Text>
        <DS.Dropdown
          fullWidth
          handleSelectionChange={([selected]) => {
            if (selected) {
              onStateChange(String(selected));
            }
          }}
          // Known dev-only hydration warning: DS.Dropdown's downshift ids are counter-based
          // and differ between SSR and hydration; DOM stays internally consistent (see README)
          labelId="bill-projection-state-label"
          options={STATE_OPTIONS}
          placeholder="Select your state"
          showSelectionInTrigger
          size="md"
          value={stateCode ? [stateCode] : []}
        />
      </DS.Flex>
    </DS.Flex>
  );
};
